import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { localeSort, uniq } from '../../../utils/Str';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface SearchComponentProps {
    searchText: string;
    setSearchText: React.Dispatch<React.SetStateAction<string>>;
}

const SearchComponent: React.FC<SearchComponentProps> = (props) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    return (
        <TextField
            id="search-text-field"
            size="small"
            fullWidth={isMobile}
            variant="outlined"
            label={t('content.safety.search')}
            value={props.searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.setSearchText(e.target.value)}
            sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: 250 }}
        />
    );
};

interface SubfleetComponentProps {
    subfleetValue: string[];
    setSubfleetValue: React.Dispatch<React.SetStateAction<string[]>>;
}

const SubfleetComponent: React.FC<SubfleetComponentProps> = (props) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { profile } = useProfile();

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            id="sub-fleet-filter"
            size="small"
            options={profile.customer.sub_fleets.filter(Boolean)}
            value={props.subfleetValue}
            onChange={(_, newValue) => {
                props.setSubfleetValue(newValue);
            }}
            renderInput={(params) => (
                <TextField {...params} label={t('content.wellness.sub_fleet')} sx={{ width: 250 }} />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                    <Checkbox
                        id={`${option}-checkbox`}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                    {option}
                </li>
            )}
            // limits the rendered tags to one chip
            renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                return (
                    <>
                        <Chip
                            size="small"
                            label={<Typography fontSize={12}>{value[0]}</Typography>}
                            {...getTagProps({ index: 0 })}
                            sx={{ borderRadius: 1, maxWidth: '160px !important' }}
                            onDelete={undefined}
                        />
                        {value.length > 1 ? <Typography variant="body2">{`+${value.length - 1}`}</Typography> : null}
                    </>
                );
            }}
            sx={{
                mr: isMobile ? 0 : 1,
                mb: isMobile ? 1 : 0,
                '& .MuiInputBase-root': { flexWrap: 'nowrap' },
            }}
        />
    );
};

interface BehaviorComponentProps {
    behaviorValue: string[];
    setBehaviorValue: React.Dispatch<React.SetStateAction<string[]>>;
    behaviorOptions: string[];
}

export const BehaviorComponent: React.FC<BehaviorComponentProps> = (props) => {
    const { t } = useTranslation();

    const setBehaviorValue = (newValue: string[]) => {
        const newBehaviorValue = props.behaviorOptions.filter((behavior) => newValue.includes(t(behavior)));
        props.setBehaviorValue(newBehaviorValue);
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            id="behaviors-item-filter"
            size="small"
            options={props.behaviorOptions
                .map((b) => t(b))
                .sort(localeSort)
                .filter(uniq)}
            value={props.behaviorValue.map((b) => t(b)).filter(uniq)}
            onChange={(_, newValue) => setBehaviorValue(newValue)}
            renderInput={(params) => (
                <TextField {...params} label={t('content.safety.behaviors')} sx={{ width: 250 }} />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} style={{ paddingLeft: 0 }}>
                    <Checkbox
                        id={`${option}-checkbox`}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                    {t(option)}
                </li>
            )}
            // limits the rendered tags to one chip
            renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                return (
                    <>
                        <Chip
                            size="small"
                            label={<Typography fontSize={12}>{t(value[0])}</Typography>}
                            {...getTagProps({ index: 0 })}
                            sx={{ borderRadius: 1, maxWidth: '160px !important' }}
                            onDelete={undefined}
                        />
                        {value.length > 1 ? <Typography variant="body2">{`+${value.length - 1}`}</Typography> : null}
                    </>
                );
            }}
            sx={{ '& .MuiInputBase-root': { flexWrap: 'nowrap' } }}
        />
    );
};

interface VehiclesToolbarComponentProps {
    searchText: string;
    setSearchText: React.Dispatch<React.SetStateAction<string>>;
    subfleetValue: string[];
    setSubfleetValue: React.Dispatch<React.SetStateAction<string[]>>;
    behaviorValue: string[];
    setBehaviorValue: React.Dispatch<React.SetStateAction<string[]>>;
    behaviorOptions: string[];
    includeAllDrivers: boolean;
    setIncludeAllDrivers: React.Dispatch<React.SetStateAction<boolean>>;
}

const DriversToolbarComponent: React.FC<VehiclesToolbarComponentProps> = (props) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                width: '100%',
                alignItems: 'center',
            }}
        >
            <SearchComponent searchText={props.searchText} setSearchText={props.setSearchText} />
            <SubfleetComponent subfleetValue={props.subfleetValue} setSubfleetValue={props.setSubfleetValue} />
            <BehaviorComponent
                behaviorValue={props.behaviorValue}
                setBehaviorValue={props.setBehaviorValue}
                behaviorOptions={props.behaviorOptions}
            />
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        checked={props.includeAllDrivers}
                        onChange={(e) => props.setIncludeAllDrivers(e.target.checked)}
                    />
                }
                label={<Typography fontSize={14}>{t('content.safety.show_all_drivers')}</Typography>}
                sx={{ ml: 1 }}
            />
        </Box>
    );
};

export default DriversToolbarComponent;
